import React, { useCallback, useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import { AlertTriangle, CalendarPlus } from "lucide-react";
import { schApi, schErr } from "@/lib/schApi";
import { useSchChanged } from "@/components/nds/windows/sch/SchWindows";
import { btnGold } from "@/components/nds/windows/finance/parts";

const SRC = { work: ["Work", "border-sky-400/50 bg-sky-500/10 text-sky-100"], calendar: ["Calendar", "border-violet-400/50 bg-violet-500/10 text-violet-100"],
  event: ["Event", "border-amber-400/50 bg-amber-500/10 text-amber-100"], vendor: ["Vendor hours", "border-emerald-400/50 bg-emerald-500/10 text-emerald-100"] };
const HOURS = Array.from({ length: 15 }, (_, i) => 7 + i);
const ymd = (iso) => iso.slice(0, 10);
const hm = (iso) => iso.slice(11, 16);
const hourOf = (iso) => Number(iso.slice(11, 13));
export const MAX_BOOK = 25;

/** A wall-clock time in America/New_York as a real Date, whatever the browser's time zone. */
const etDate = (day, hour) => {
  const [y, m, d] = day.split("-").map(Number);
  let t = Date.UTC(y, m - 1, d, hour + 5);
  for (let i = 0; i < 2; i += 1) {
    const h = Number(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", hourCycle: "h23" }).format(new Date(t)));
    t += (hour - h) * 3600000;
  }
  return new Date(t);
};

const Item = ({ it }) => {
  const [label, cls] = SRC[it.source] || SRC.event;
  return (
    <div data-testid={`sch-grid-item-${it.source}-${it.id}`} title={`${label} · ${it.title} · ${hm(it.start)}–${hm(it.end)} ET`}
      className={`px-1 py-0.5 rounded-sm border text-[10px] leading-tight ${cls}`}>
      <div className="flex items-center gap-1">
        {it.conflict && <AlertTriangle className="h-3 w-3 text-red-300 shrink-0" data-testid={`sch-grid-conflict-${it.id}`} />}
        <span className="truncate">{it.source === "vendor" ? it.title : `${hm(it.start)} ${it.title}`}</span>
      </div>
      {it.unconfirmed && <span data-testid={`sch-grid-unconfirmed-${it.id}`} className="font-mono text-[8px] uppercase text-amber-300">unconfirmed</span>}
    </div>
  );
};

/** Side-by-side grid: rows = selected residents, columns = Mon–Sun (or hours of one day). Merged server-side; click cells to pick, then Book. */
export default function ResidentGrid({ anchor, dayMode, residentIds, onBook }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [rows, setRows] = useState([]);
  const [cells, setCells] = useState([]);
  const mon = addDays(anchor, -((anchor.getDay() + 6) % 7));
  const cols = dayMode ? HOURS.map((h) => ({ key: `${format(anchor, "yyyy-MM-dd")}T${String(h).padStart(2, "0")}`, label: format(new Date(2000, 0, 1, h), "ha") }))
    : Array.from({ length: 7 }, (_, i) => addDays(mon, i)).map((x) => ({ key: format(x, "yyyy-MM-dd"), label: format(x, "EEE d") }));
  const start = dayMode ? format(anchor, "yyyy-MM-dd") : cols[0].key;
  const end = dayMode ? start : cols[6].key;
  const ids = residentIds.join(",");
  const [tick, setTick] = useState(0);
  useSchChanged(useCallback(() => { setTick((n) => n + 1); setCells([]); setRows([]); }, []));
  useEffect(() => {
    if (!ids) { setD({ residents: [] }); return; }
    setErr(null);
    schApi.get("/resident-grid", { resident_ids: ids, start, end }).then(setD).catch((e) => setErr(schErr(e)));
  }, [ids, start, end, tick]);
  useEffect(() => { setCells([]); setRows([]); }, [start, end, dayMode]);
  if (!ids) return <p data-testid="sch-grid-empty" className="p-6 text-sm text-nex-muted">Pick residents (or a quick group) in the filters to compare their schedules side by side.</p>;
  if (err) return <p data-testid="sch-grid-error" className="p-6 text-sm text-red-300">{err}</p>;
  if (!d) return <p data-testid="sch-grid-loading" className="p-6 text-sm text-nex-muted">Loading schedules…</p>;
  const inCol = (it, c) => (dayMode ? ymd(it.start) === start && hourOf(it.start) <= Number(c.key.slice(11)) && Number(c.key.slice(11)) < Math.max(hourOf(it.end) + (it.end.slice(14, 16) !== "00" ? 1 : 0), hourOf(it.start) + 1)
    : ymd(it.start) === c.key);
  const cellKey = (rid, c) => `${rid}|${c.key}`;
  const toggleCell = (rid, c) => setCells((s) => (s.includes(cellKey(rid, c)) ? s.filter((x) => x !== cellKey(rid, c)) : [...s, cellKey(rid, c)]));
  const pickedResidents = [...new Set([...rows, ...cells.map((k) => k.split("|")[0])])];
  const book = () => {
    const first = cells.map((k) => k.split("|")[1]).sort()[0];
    const startAt = first ? etDate(first.slice(0, 10), first.length > 10 ? Number(first.slice(11, 13)) : 9) : etDate(start, 9);
    onBook(pickedResidents, startAt);
    setCells([]); setRows([]);
  };
  const over = pickedResidents.length > MAX_BOOK;
  return (
    <div data-testid="sch-grid" className="flex flex-col min-h-0">
      <div className="px-4 py-2 flex flex-wrap items-center gap-3 border-b border-white/10 text-[11px]">
        {Object.entries(SRC).map(([k, [l, cls]]) => <span key={k} className={`px-1.5 py-0.5 rounded-sm border ${cls}`}>{l}</span>)}
        <span className="inline-flex items-center gap-1 text-nex-muted"><AlertTriangle className="h-3 w-3 text-red-300" /> overlap</span>
        {d.dropped?.length > 0 && <span data-testid="sch-grid-dropped" className="text-amber-300">{d.dropped.length} no longer active — removed</span>}
        <span className="ml-auto text-nex-muted" data-testid="sch-grid-picked">{pickedResidents.length} resident{pickedResidents.length === 1 ? "" : "s"} · {cells.length} cell{cells.length === 1 ? "" : "s"} picked</span>
        {over && <span data-testid="sch-grid-over" className="text-red-300">One booking can include at most {MAX_BOOK} residents</span>}
        <button type="button" disabled={!pickedResidents.length || over} onClick={book} data-testid="sch-grid-book" className={`${btnGold} inline-flex items-center gap-1 disabled:opacity-40`}><CalendarPlus className="h-3 w-3" /> Book</button>
      </div>
      <div className="overflow-auto">
        <table className="text-xs border-collapse min-w-full" data-testid="sch-grid-table">
          <thead><tr>
            <th className="sticky left-0 z-10 bg-[#0b0f1f] text-left px-2 py-1.5 border-b border-white/10 min-w-[150px] text-nex-muted font-normal">Resident</th>
            {cols.map((c) => <th key={c.key} className="px-2 py-1.5 border-b border-l border-white/10 text-nex-muted font-normal min-w-[110px] whitespace-nowrap">{c.label}</th>)}
          </tr></thead>
          <tbody>{d.residents.map((r) => (
            <tr key={r.resident_id} data-testid={`sch-grid-row-${r.resident_id}`}>
              <td className="sticky left-0 z-10 bg-[#0b0f1f] px-2 py-1.5 border-b border-white/5 align-top">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={rows.includes(r.resident_id)} onChange={() => setRows((s) => (s.includes(r.resident_id) ? s.filter((x) => x !== r.resident_id) : [...s, r.resident_id]))}
                    data-testid={`sch-grid-rowpick-${r.resident_id}`} className="accent-[#d4a017]" />
                  <span className="text-white/90">{r.name}</span>
                </label>
              </td>
              {cols.map((c) => {
                const its = r.items.filter((it) => inCol(it, c));
                const on = cells.includes(cellKey(r.resident_id, c));
                return (
                  <td key={c.key} onClick={() => toggleCell(r.resident_id, c)} data-testid={`sch-grid-cell-${r.resident_id}-${c.key}`}
                    className={`px-1 py-1 border-b border-l border-white/5 align-top cursor-pointer space-y-0.5 ${on ? "bg-nex-gold/15 ring-1 ring-nex-gold/60" : its.length ? "" : "hover:bg-emerald-500/5"}`}>
                    {its.length ? its.map((it) => <Item key={`${it.source}-${it.id}`} it={it} />) : <span className="text-[10px] text-emerald-300/50">Free</span>}
                  </td>
                );
              })}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
